/** Server-driven Answer or Bluff payloads (`type: "bluff_game"`). */

export type BluffGamePhase =
  | "lobby"
  | "question"
  | "answer"
  | "vote"
  | "leaderboard"
  | "game_over";

export type BluffVoteOption = { id: number; text: string };

export type BluffGamePayload = {
  type: "bluff_game";
  phase: BluffGamePhase;
  round_index: number;
  phase_ends_at: string | null;
  scores: Record<string, number>;
  players: Record<string, string>;
  current_prompt: string | null;
  current_question_id: number | null;
  /** When set by the server, guests can block submissions that match the real answer (same normalization as host deck). */
  canonical_answer?: string | null;
  last_results: BluffLastResults | null;
  vote_options?: BluffVoteOption[];
};

export type BluffLastResults = {
  votes_cast: number;
  vote_breakdown: Array<{
    voter: string;
    choice_id: number;
    points_to_voter: number;
    points_to_author: number;
    picked_truth: boolean;
    author?: string | null;
  }>;
  scores: Record<string, number>;
};

const PHASES: BluffGamePhase[] = [
  "lobby",
  "question",
  "answer",
  "vote",
  "leaderboard",
  "game_over",
];

export function parseBluffGamePayload(raw: unknown): BluffGamePayload | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.type !== "bluff_game") return null;
  const phase = o.phase as BluffGamePhase;
  if (!PHASES.includes(phase)) return null;
  const round_index = typeof o.round_index === "number" ? o.round_index : Number(o.round_index) || -1;
  const phase_ends_at =
    o.phase_ends_at === null || o.phase_ends_at === undefined
      ? null
      : typeof o.phase_ends_at === "string"
        ? o.phase_ends_at
        : null;
  const scores: Record<string, number> = {};
  if (o.scores && typeof o.scores === "object") {
    for (const [k, v] of Object.entries(o.scores as Record<string, unknown>)) {
      if (typeof v === "number") scores[k] = v;
      else if (typeof v === "string" && v.trim() !== "") scores[k] = Number(v) || 0;
    }
  }
  const players: Record<string, string> = {};
  if (o.players && typeof o.players === "object") {
    for (const [k, v] of Object.entries(o.players as Record<string, unknown>)) {
      if (typeof v === "string") players[k] = v;
    }
  }
  const current_prompt = typeof o.current_prompt === "string" ? o.current_prompt : null;
  const current_question_id =
    typeof o.current_question_id === "number" ? o.current_question_id : o.current_question_id != null ? Number(o.current_question_id) : null;
  let canonical_answer: string | null = null;
  if (o.canonical_answer != null) {
    if (typeof o.canonical_answer === "string") {
      canonical_answer = o.canonical_answer;
    } else if (typeof o.canonical_answer === "number" && Number.isFinite(o.canonical_answer)) {
      canonical_answer = String(o.canonical_answer);
    }
  }

  let vote_options: BluffVoteOption[] | undefined;
  if (Array.isArray(o.vote_options)) {
    vote_options = o.vote_options
      .map((row) => {
        if (!row || typeof row !== "object") return null;
        const r = row as Record<string, unknown>;
        const id = typeof r.id === "number" ? r.id : Number(r.id);
        const text = typeof r.text === "string" ? r.text : "";
        if (!Number.isFinite(id) || !text) return null;
        return { id, text };
      })
      .filter(Boolean) as BluffVoteOption[];
  }

  let last_results: BluffLastResults | null = null;
  if (o.last_results && typeof o.last_results === "object") {
    const lr = o.last_results as Record<string, unknown>;
    const votes_cast = typeof lr.votes_cast === "number" ? lr.votes_cast : Number(lr.votes_cast) || 0;
    const vb = Array.isArray(lr.vote_breakdown) ? lr.vote_breakdown : [];
    const resultScores: Record<string, number> = {};
    if (lr.scores && typeof lr.scores === "object") {
      for (const [k, v] of Object.entries(lr.scores as Record<string, unknown>)) {
        if (typeof v === "number") resultScores[k] = v;
      }
    }
    last_results = {
      votes_cast,
      vote_breakdown: vb as BluffLastResults["vote_breakdown"],
      scores: resultScores,
    };
  }

  return {
    type: "bluff_game",
    phase,
    round_index,
    phase_ends_at,
    scores,
    players,
    current_prompt,
    current_question_id: Number.isFinite(current_question_id as number) ? (current_question_id as number) : null,
    canonical_answer,
    last_results,
    vote_options,
  };
}
